export type VehicleTypeCode = 'CAR' | 'MOTORCYCLE' | 'MOTORCYCLE_LOW' | 'MOTORCYCLE_HIGH' | 'TRUCK';
export type PriceRangeCode = 'UP_TO_50K' | 'FROM_50K_TO_100K' | 'FROM_100K_TO_200K' | 'ABOVE_200K';
export type FipeDiscountRangeCode = 'UP_TO_15' | 'FROM_15_TO_25' | 'FROM_25_TO_35' | 'ABOVE_35';
export type RegionPreferenceCode = 'DEALER_CITY' | 'CITY_RADIUS' | 'DEALER_STATE' | 'ALL_COUNTRY';
export type VehicleConditionPreferenceCode =
  | 'APPROVED_REPORT_LOW_KM'
  | 'LIGHT_NOTES'
  | 'REPAIR_DAMAGE_OR_HIGH_KM';

export type ProfileTypeId = 'MARGIN_HUNTER' | 'FAST_TURNOVER' | 'BALANCED' | 'OPPORTUNISTIC';

export type PurchaseProfileQuestionId =
  | 'vehicleTypes'
  | 'priceRanges'
  | 'fipeDiscountRanges'
  | 'region'
  | 'vehicleCondition';

export interface PurchaseProfileAnswers {
  vehicleTypes?: string[];
  priceRanges?: string[];
  fipeDiscountRanges?: string[];
  region?: string;
  vehicleCondition?: string;
}

export interface DealerPurchaseProfileRequest {
  vehicleTypes: string[];
  priceRanges: string[];
  fipeDiscountRanges: string[];
  region: string;
  vehicleCondition: string;
}

export interface PurchaseProfileOptionItem {
  id: string;
  name: string;
}

export interface PurchaseProfileOptions {
  vehicleTypes: PurchaseProfileOptionItem[];
  priceRanges: PurchaseProfileOptionItem[];
  fipeDiscountRanges: PurchaseProfileOptionItem[];
  regions: PurchaseProfileOptionItem[];
  vehicleConditions: PurchaseProfileOptionItem[];
}

export interface PurchaseProfileOnboarding {
  status: 'PENDING' | 'COMPLETED';
  shouldShowModal: boolean;
  nextShowAt?: string;
  lastSavedAt?: string;
  lastSkippedAt?: string;
}

export interface ProfileTypeInfo {
  id: ProfileTypeId | string;
  name: string;
}

export interface DealerCityInfo {
  id: string;
  name: string;
}

export interface PurchaseProfileVehicleFilter {
  segments?: string[];
  priceStart?: number;
  priceEnd?: number;
  fipeDiffStart?: number;
  fipeDiffEnd?: number;
  cityId?: string;
  cityRadius?: number;
  uf?: string;
  reportStatuses?: string[];
  kmEnd?: number;
  excludeDamaged?: boolean;
}

export interface DealerPurchaseProfileResponse {
  answers?: PurchaseProfileAnswers;
  profileType?: ProfileTypeInfo;
  savedAt?: string;
  suggested: boolean;
  options: PurchaseProfileOptions;
  dealerCity?: DealerCityInfo;
  dealerUf?: string;
  onboarding: PurchaseProfileOnboarding;
  vehicleFilter?: PurchaseProfileVehicleFilter;
}

export interface FipeAverageResponse {
  average: number;
}


export interface PurchaseProfileTextPart {
  text: string;
  isBold: boolean;
}

export interface PurchaseProfileDefinition {
  key: ProfileTypeId;
  name: string;
  icon: string;
  headline: string;
  parts: PurchaseProfileTextPart[];
}

export interface PurchaseProfileOption {
  id: string;
  label: string;
  sub?: string;
  icon: string;
}

export interface PurchaseProfileQuestion {
  id: PurchaseProfileQuestionId;
  title: string;
  desc: string;
  multi: boolean;
  label: string;
  icon: string;
  summaryLabel?: string;
  summarySuffix?: string;
  insight?: boolean;
  locNote?: boolean;
  options: PurchaseProfileOption[];
}

export const DEFAULT_PURCHASE_PROFILE_ANSWERS: PurchaseProfileAnswers = {
  vehicleTypes: [],
  priceRanges: [],
  fipeDiscountRanges: [],
  region: undefined,
  vehicleCondition: undefined,
};

export const OPTION_VISUAL_META: Record<string, { icon: string; sub?: string }> = {

  CAR: { icon: 'fa-car-side', sub: 'Carros, SUVs e picapes' },
  MOTORCYCLE: { icon: 'fa-motorcycle', sub: 'Baixa e alta cilindrada' },
  MOTORCYCLE_LOW: { icon: 'fa-motorcycle', sub: 'Até 300cc' },
  MOTORCYCLE_HIGH: { icon: 'fa-motorcycle', sub: 'Acima de 300cc' },
  TRUCK: { icon: 'fa-truck', sub: 'Caminhões e implementos' },

  UP_TO_50K: { icon: 'fa-coins' },
  FROM_50K_TO_100K: { icon: 'fa-money-bill' },
  FROM_100K_TO_200K: { icon: 'fa-money-bill-wave' },
  ABOVE_200K: { icon: 'fa-gem' },

  UP_TO_15: { icon: 'fa-bolt', sub: 'Ganho no giro, não na margem' },
  FROM_15_TO_25: { icon: 'fa-scale-balanced', sub: 'Na média da plataforma' },
  FROM_25_TO_35: { icon: 'fa-arrow-trend-down', sub: 'Busco margem acima da média' },
  ABOVE_35: { icon: 'fa-piggy-bank', sub: 'Só entro com margem gorda' },

  DEALER_CITY: { icon: 'fa-city' },
  CITY_RADIUS: { icon: 'fa-location-crosshairs' },
  DEALER_STATE: { icon: 'fa-map' },
  ALL_COUNTRY: { icon: 'fa-earth-americas', sub: 'O frete eu resolvo' },

  APPROVED_REPORT_LOW_KM: { icon: 'fa-circle-check', sub: 'Quero o carro pronto pra vender' },
  LIGHT_NOTES: { icon: 'fa-screwdriver-wrench', sub: 'Um reparo simples não me assusta' },
  REPAIR_DAMAGE_OR_HIGH_KM: { icon: 'fa-car-burst', sub: 'Se o preço compensar' },
};

export const PURCHASE_PROFILES: Record<ProfileTypeId, PurchaseProfileDefinition> = {
  MARGIN_HUNTER: {
    key: 'MARGIN_HUNTER',
    name: 'Caçador de margem',
    icon: 'fa-arrow-trend-down',
    headline: 'Compra bem abaixo da FIPE e prepara o veículo',
    parts: [
      { text: 'Busca ', isBold: false },
      { text: 'os maiores descontos', isBold: true },
      { text: ', aceita ', isBold: false },
      { text: 'veículos que precisam de reparo', isBold: true },
      { text: ' e ganha ', isBold: false },
      { text: 'na margem de cada negócio', isBold: true },
      { text: '.', isBold: false },
    ],
  },
  BALANCED: {
    key: 'BALANCED',
    name: 'Equilibrado',
    icon: 'fa-scale-balanced',
    headline: 'Busca margem sem abrir mão do giro',
    parts: [
      { text: 'Compra com ', isBold: false },
      { text: 'bom desconto', isBold: true },
      { text: ', prefere ', isBold: false },
      { text: 'veículos com liquidez', isBold: true },
      { text: ' e equilibra ', isBold: false },
      { text: 'margem e velocidade', isBold: true },
      { text: '.', isBold: false },
    ],
  },
  FAST_TURNOVER: {
    key: 'FAST_TURNOVER',
    name: 'Giro rápido',
    icon: 'fa-bolt',
    headline: 'Prefere vender rápido a esperar a margem perfeita',
    parts: [
      { text: 'Compra veículos ', isBold: false },
      { text: 'prontos para revenda', isBold: true },
      { text: ', aceita ', isBold: false },
      { text: 'descontos dentro da média do mercado', isBold: true },
      { text: ' e ganha no ', isBold: false },
      { text: 'volume e na velocidade do giro', isBold: true },
      { text: '.', isBold: false },
    ],
  },
  OPPORTUNISTIC: {
    key: 'OPPORTUNISTIC',
    name: 'Oportunista',
    icon: 'fa-magnifying-glass',
    headline: 'Avalia cada negócio pelo que ele é',
    parts: [
      { text: 'Olha ', isBold: false },
      { text: 'caso a caso', isBold: true },
      { text: ', compra quando ', isBold: false },
      { text: 'a conta fecha', isBold: true },
      { text: ' e quer ver ', isBold: false },
      { text: 'um pouco de tudo', isBold: true },
      { text: ' dentro das suas faixas.', isBold: false },
    ],
  },
};

export const DEFAULT_PURCHASE_PROFILE_OPTIONS: PurchaseProfileOptions = {
  vehicleTypes: [
    { id: 'CAR', name: 'Leves' },
    { id: 'TRUCK', name: 'Pesados' },
    { id: 'MOTORCYCLE', name: 'Motos' },
  ],
  priceRanges: [
    { id: 'UP_TO_50K', name: 'Até R$ 50 mil' },
    { id: 'FROM_50K_TO_100K', name: 'De R$ 50 a 100 mil' },
    { id: 'FROM_100K_TO_200K', name: 'De R$ 100 a 200 mil' },
    { id: 'ABOVE_200K', name: 'Acima de R$ 200 mil' },
  ],
  fipeDiscountRanges: [
    { id: 'UP_TO_15', name: 'Até 15% abaixo' },
    { id: 'FROM_15_TO_25', name: 'De 15% a 25% abaixo' },
    { id: 'FROM_25_TO_35', name: 'De 25% a 35% abaixo' },
    { id: 'ABOVE_35', name: 'Mais de 35% abaixo' },
  ],
  regions: [
    { id: 'DEALER_CITY', name: 'Só na minha cidade' },
    { id: 'CITY_RADIUS', name: 'Até 200 km da loja' },
    { id: 'DEALER_STATE', name: 'No meu estado' },
    { id: 'ALL_COUNTRY', name: 'Brasil todo' },
  ],
  vehicleConditions: [
    { id: 'APPROVED_REPORT_LOW_KM', name: 'Só laudo aprovado e km baixo' },
    { id: 'LIGHT_NOTES', name: 'Aceito apontamentos leves' },
    { id: 'REPAIR_DAMAGE_OR_HIGH_KM', name: 'Aceito reparo, sinistro ou km alto' },
  ],
};

export function buildPurchaseProfileQuestions(
  options: PurchaseProfileOptions = DEFAULT_PURCHASE_PROFILE_OPTIONS,
  context?: { dealerCity?: DealerCityInfo; dealerUf?: string }
): PurchaseProfileQuestion[] {
  const mapItems = (items: PurchaseProfileOptionItem[], questionId: PurchaseProfileQuestionId): PurchaseProfileOption[] => {
    return (items || []).map((item) => {
      const meta = OPTION_VISUAL_META[item.id] || { icon: 'fa-circle' };
      let sub = meta.sub;

      if (questionId === 'region') {
        if (item.id === 'DEALER_CITY' && context?.dealerCity?.name) {
          sub = context.dealerUf ? `${context.dealerCity.name}/${context.dealerUf}` : context.dealerCity.name;
        } else if (item.id === 'CITY_RADIUS') {
          sub = 'A partir da cidade da loja';
        } else if (item.id === 'DEALER_STATE' && context?.dealerUf) {
          sub = `Todo o estado de ${context.dealerUf}`;
        }
      }

      return {
        id: item.id,
        label: item.name,
        sub,
        icon: meta.icon,
      };
    });
  };

  return [
    {
      id: 'vehicleTypes',
      title: 'Que tipo de veículo sua loja compra?',
      desc: 'Pode escolher mais de um.',
      multi: true,
      summaryLabel: 'Seus tipos',
      label: 'Tipo de veículo',
      icon: 'fa-car',
      options: mapItems(options.vehicleTypes, 'vehicleTypes'),
    },
    {
      id: 'priceRanges',
      title: 'Com que faixas de valor você trabalha?',
      desc: 'Pode marcar mais de uma. Ex.: até R$ 50 mil e de R$ 50 a 100 mil.',
      multi: true,
      summaryLabel: 'Sua faixa',
      label: 'Faixa de valor',
      icon: 'fa-brazilian-real-sign',
      options: mapItems(options.priceRanges, 'priceRanges'),
    },
    {
      id: 'fipeDiscountRanges',
      title: 'Com que desconto frente à FIPE você compra?',
      desc: 'Pode marcar mais de uma faixa.',
      multi: true,
      insight: true,
      summaryLabel: 'Sua faixa',
      label: 'Desconto na FIPE',
      summarySuffix: ' da FIPE',
      icon: 'fa-percent',
      options: mapItems(options.fipeDiscountRanges, 'fipeDiscountRanges'),
    },
    {
      id: 'region',
      title: 'Até onde você busca veículos?',
      desc: 'A partir do endereço da sua loja.',
      multi: false,
      locNote: true,
      label: 'Região',
      icon: 'fa-map-location-dot',
      options: mapItems(options.regions, 'region'),
    },
    {
      id: 'vehicleCondition',
      title: 'Que condição de veículo você topa?',
      desc: 'Pense no que sua oficina e sua equipe conseguem resolver.',
      multi: false,
      label: 'Condição',
      icon: 'fa-clipboard-check',
      options: mapItems(options.vehicleConditions, 'vehicleCondition'),
    },
  ];
}
